
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TemplatesList } from "./templates/TemplatesList";
import { TemplateEditor } from "./templates/TemplateEditor";
import { TemplatePreview } from "./templates/TemplatePreview";
import { SendTestEmailDialog } from "./templates/SendTestEmailDialog";
import { useNewsletterTemplates } from "@/hooks/useNewsletterTemplates";
import { toast } from "sonner";

interface Template {
  id: string;
  name: string;
  subject: string;
  preview_text?: string;
  content: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

interface TemplateFormData {
  id?: string;
  name: string;
  subject: string;
  previewText?: string;
  content: string;
}

export const TemplateManagement = () => {
  const [currentView, setCurrentView] = useState<"list" | "edit">("list");
  const [currentTemplate, setCurrentTemplate] = useState<TemplateFormData | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isTestEmailDialogOpen, setIsTestEmailDialogOpen] = useState(false);
  
  const { 
    templates, 
    isLoading, 
    saveTemplate, 
    deleteTemplate, 
    duplicateTemplate,
    sendTestEmail
  } = useNewsletterTemplates();

  const handleCreateTemplate = () => {
    setCurrentTemplate({
      name: "",
      subject: "",
      previewText: "",
      content: ""
    });
    setCurrentView("edit");
  };

  const handleEditTemplate = (template: Template) => {
    setCurrentTemplate({
      id: template.id,
      name: template.name,
      subject: template.subject,
      previewText: template.preview_text,
      content: template.content
    });
    setCurrentView("edit");
  };

  const handleSaveTemplate = async (template: TemplateFormData) => {
    try {
      await saveTemplate({
        id: template.id,
        name: template.name,
        subject: template.subject,
        previewText: template.previewText,
        content: template.content
      });
      setCurrentView("list");
      return true;
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
      return false;
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      await deleteTemplate(templateId);
      toast.success("Template deleted successfully");
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };

  const handleDuplicateTemplate = async (template: Template) => {
    try {
      await duplicateTemplate(template);
      toast.success(`Template "${template.name}" duplicated successfully`);
    } catch (error) {
      console.error("Error duplicating template:", error);
      toast.error("Failed to duplicate template");
    }
  };

  const handlePreviewTemplate = (template: TemplateFormData) => {
    setCurrentTemplate(template);
    setIsPreviewOpen(true);
  };

  const handleTestTemplate = (template: TemplateFormData) => {
    setCurrentTemplate(template);
    setIsTestEmailDialogOpen(true);
  };

  const handleSendTestEmail = async (email: string) => {
    if (!currentTemplate?.id) {
      toast.error("Template must be saved before sending a test email");
      return;
    }
    
    try {
      await sendTestEmail(currentTemplate.id, email);
      return;
    } catch (error) {
      console.error("Error sending test email:", error);
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-6">
          {currentView === "list" ? (
            <TemplatesList
              templates={templates}
              isLoading={isLoading}
              onCreateTemplate={handleCreateTemplate}
              onEditTemplate={handleEditTemplate}
              onDeleteTemplate={handleDeleteTemplate}
              onDuplicateTemplate={handleDuplicateTemplate}
            />
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-slate-800">
                  {currentTemplate?.id ? "Edit" : "Create"} Email Template
                </h3>
                <button
                  onClick={() => setCurrentView("list")}
                  className="text-sm text-slate-500 hover:text-slate-900"
                >
                  ← Back to templates
                </button>
              </div>
              
              <TemplateEditor
                initialData={currentTemplate || undefined}
                onSave={handleSaveTemplate}
                onPreview={handlePreviewTemplate}
                onTest={handleTestTemplate}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {isPreviewOpen && currentTemplate && (
        <TemplatePreview
          template={currentTemplate}
          onClose={() => setIsPreviewOpen(false)}
        />
      )}

      <SendTestEmailDialog
        isOpen={isTestEmailDialogOpen}
        onClose={() => setIsTestEmailDialogOpen(false)}
        onSend={handleSendTestEmail}
        templateName={currentTemplate?.name || ""}
      />
    </div>
  );
};
